import React, { memo, useState, useCallback, useImperativeHandle } from 'react';
import { CourseBuilder } from '../types';
import { useNonNilObservable } from '@core/utils/hooks/rxjs';
import { classnames } from '@core/utils/css';
import { CSSTransition } from 'react-transition-group';
import { R } from '@core/utils/r';
import { VariationSelector } from './VariationSelector';
import { usePopperAnchor } from '@core/components/Popper';
import { CourseBuilderBlockFormData, useCourseBuilderContext, useCourseBuilderBlockContext } from '../../CourseBuilderPage';
import { openAddCourseBuilderWidgetOverlay } from '../../CourseBuilderPage/Components/AddWidgetOverlay';
import './base.scss';
export interface BlockToolbarProps {
  menuOrder: number;
  isLast: boolean;
  onDelete?: () => void;
  onCopy?: () => void;
  onEdit?: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onSection?: () => void;
  block: CourseBuilderBlockFormData;
  onVariationChange: (variation: string) => void;
  partiallyEditable?: boolean;
  onCopyBlock: () => void;
}

export const BlockToolbar = memo((props: BlockToolbarProps) => {
  const {
    menuOrder,
    isLast,
    onDelete,
    onCopy,
    onEdit,
    onMoveUp,
    onMoveDown,
    block,
    onVariationChange,
    onSection,
    onCopyBlock,
    partiallyEditable = false,
  } = props;
  const config = CourseBuilder.BLOCK_CONFIGS[block.type];
  const hasVariations = config.variations.length > 1;
  const anchor = usePopperAnchor();

  return (
    <div className="block-toolbar" style={{ fontFamily: 'Poppins' }}>
      <div className="block-toolbar__left">
        <div className="block-toolbar__item" onClick={anchor.onClick}>
          {R.startCase(block.variation)}
          {hasVariations && <i className="ml-2 fa fa-chevron-down"></i>}
        </div>
        {hasVariations && <VariationSelector anchor={anchor} config={config} variation={block.variation} onVariationChange={onVariationChange} />}
        <div className="block-toolbar__item" onClick={onEdit}>
          Edit
        </div>
        <div className="block-toolbar__item" onClick={onSection}>
          {block.isSection ? (
            <div>
              <i className="fa fa-check"></i> Section
            </div>
          ) : (
            'Make Section'
          )}
        </div>
      </div>
      <div className="block-toolbar__right">
        <div className="block-toolbar__item" onClick={onCopyBlock}>
          <i className="fa fa-plus"></i>
        </div>
        {menuOrder > 0 && (
          <div className="block-toolbar__item" onClick={onMoveUp}>
            <i className="fa fa-arrow-up"></i>
          </div>
        )}
        {!isLast && (
          <div className="block-toolbar__item" onClick={onMoveDown}>
            <i className="fa fa-arrow-down"></i>
          </div>
        )}
        <div className="block-toolbar__item" onClick={onCopy}>
          <i className="fa fa-copy"></i>
        </div>
        <div className="block-toolbar__item" onClick={onDelete}>
          <i className="fa fa-trash-alt"></i>
        </div>
      </div>
    </div>
  );
});

export interface AddButtonProps {
  onAdd: (type: CourseBuilder.BlockType, variation: string) => void;
}

export const AddButton = memo((props: AddButtonProps) => {
  const { onAdd } = props;

  return (
    <div className="pb-add" onClick={() => openAddCourseBuilderWidgetOverlay({ onAdd })}>
      <i className="fa fa-plus"></i>
    </div>
  );
});

export interface BlockBaseHandle {
  openSettings(): void;
}

export interface BlockBaseProps {
  isLast: boolean;
  isFirst: boolean;
  onAdd(type: CourseBuilder.BlockType, variation: string, at?: number): void;
  onDelete(id: string): void;
  onMove(id: string, direction: number): void;
  onCopy(id: string): void;
  lessonId?: string;
  onCopyBlock(id: string): void;
  handleRef?: React.Ref<BlockBaseHandle>;
}

export const BlockBase = React.forwardRef<HTMLDivElement, BlockBaseProps>((props, forwardRef) => {
  const { isLast, isFirst, onAdd, onDelete, onMove, onCopy, lessonId, onCopyBlock } = props;
  const { form } = useCourseBuilderContext()!;
  const { blockForm, blockIndex } = useCourseBuilderBlockContext()!;
  const [hover, setHover] = useState(false);
  const formValue = useNonNilObservable(blockForm.value$);
  const config = CourseBuilder.BLOCK_CONFIGS[formValue.type];
  const handleRef = React.useRef<CourseBuilder.BlockHandle>(null);

  const openSettings = useCallback(() => handleRef.current?.openSettings(), [handleRef]);
  useImperativeHandle(props.handleRef, () => ({ openSettings }), [openSettings]);

  const onSection = useCallback(() => {
    blockForm.patchValue({ isSection: !formValue.isSection });
  }, [blockForm, formValue]);

  if (!config) return null;
  const { component: BlockComponent } = config;

  return (
    <div
      id={formValue.id}
      ref={forwardRef}
      className={classnames('base-block-container', { hover, isLast })}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <BlockComponent id={formValue.id} variation={formValue.variation} editable handlesRef={handleRef} lessonId={lessonId} />
      <CSSTransition in={hover} timeout={300} classNames="block-toolbar-animate" unmountOnExit>
        <BlockToolbar
          menuOrder={formValue.menuOrder}
          isLast={form.value.blocks.length === blockIndex + 1}
          onDelete={() => onDelete(formValue.id)}
          onMoveUp={() => onMove(formValue.id, -1)}
          onMoveDown={() => onMove(formValue.id, 1)}
          onCopy={() => onCopy(formValue.id)}
          onSection={() => onSection()}
          block={formValue}
          onVariationChange={variation => blockForm.patchValue({ variation })}
          onEdit={() => handleRef?.current?.openSettings()}
          onCopyBlock={() => onCopyBlock(formValue.id)}
        />
      </CSSTransition>
      {!isLast && (
        <CSSTransition in={hover} timeout={300} classNames="block-toolbar-add-animate" unmountOnExit>
          <AddButton onAdd={(type, variation) => onAdd(type, variation, formValue.menuOrder + 1)} />
        </CSSTransition>
      )}
    </div>
  );
});
